'use client';

import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { DashWidget, Plant, Device } from '@/lib/types';
import { useDeviceLatest, LatestResponse } from '@/lib/use-device-latest';
import { NO_VALUE } from '@/lib/device-status';

interface Props { widget: DashWidget; plant: Plant; }

// Overlay a device's live 30s snapshot onto its plant.devices entry. plant.devices
// is loaded once at login and never re-fetched, so without this the schematic is
// frozen. Keeps the base device when no live snapshot has arrived yet. The keys,
// field names and units line up with buildDeviceData(), so findVal/findName read
// the fresh values unchanged.
function mergeLive(base: Device | undefined, latest: LatestResponse | null): Device | undefined {
  if (!base || !latest) return base;
  const data: Record<string, number> = {};
  const fieldNames: Record<string, string> = {};
  const units: Record<string, string> = {};
  for (const [k, f] of Object.entries(latest.data)) {
    // null = offline, the reading is stale: leave the key out so it shows "—".
    if (f.value != null) data[k] = f.value;
    fieldNames[k] = f.name;
    if (f.unit) units[k] = f.unit;
  }
  if (latest.energy_usage_day != null) data.daily = latest.energy_usage_day;
  if (latest.energy_usage_month != null) data.monthly = latest.energy_usage_month;
  return { ...base, st: latest.status === 'ONLINE' ? 'online' : 'offline', data, fieldNames, units };
}

// Wastewater pump-station SCADA for เทศบาลนครแหลมฉบัง. Pulls all linked Mtech
// devices from plant.devices. Field names are maintained on site and reworded
// periodically, so status/current channels are matched on the significant words
// of the meter's own field name (see findVal/findName), not a fixed key:
//   Mtech8_Sensor  → water quality: do, ph, cod, bod, toc, turbidity (mg/L,
//                    except pH; the sensor's tbl_config_data carries no unit)
//   MTech8Pump     → eight-load panel on/off + overload (Sump2 P1/P2, EQ1/2,
//                    Sludge, AE1/2, SED). One [OnOff, Over] pair per load.
//   MTech8p_131_1/2/3 → per-load current for the same eight loads
//   MTech8p_2230   → eight-load panel total energy (shown as Zone B)
//   Mtech2Pump     → Sump 1 panel on/off + overload (Sump1 P1/P2)
//   MTech2p_131    → Sump 1 panel current
//   Mtech2p_2230   → Sump 1 panel total energy (shown as Zone A)
// The energy cards run in process order (Sump 1 = Zone A, downstream = Zone B),
// the reverse of the internal zoneA/zoneB pump arrays. Values are null until the
// devices report; the widget degrades to "–".
export default function WwtpScadaWidget({ widget, plant }: Props) {
  const lang = useAppStore((s) => s.lang);
  const th = lang === 'th';
  // Non-destructive showcase mode: when widget.demo is set (or every device is
  // dark) the schematic runs on representative values instead of live telemetry,
  // so the SCADA can be demoed without writing to real device tables or tripping
  // their alarms. Live data automatically takes over once the meters report.
  const demo = !!(widget as unknown as { demo?: boolean }).demo;
  // duty/standby scenario with one overload alarm (เติมอากาศ #2)
  const DA = [
    { on: 1, ov: 0, a: 12.4 }, { on: 0, ov: 0, a: 0 }, { on: 1, ov: 0, a: 10.2 }, { on: 0, ov: 0, a: 0 },
    { on: 1, ov: 0, a: 9.1 }, { on: 0, ov: 0, a: 0 }, { on: 1, ov: 0, a: 8.6 }, { on: 0, ov: 1, a: 0 },
  ];
  const DB2 = [{ on: 1, ov: 0, a: 9.2 }, { on: 1, ov: 0, a: 7.4 }];

  const byName = (n: string): Device | undefined => plant.devices.find((d) => d.name?.toLowerCase() === n.toLowerCase());
  const val = (dev: Device | undefined, key: string): number | null => {
    const v = dev?.data?.[key as keyof typeof dev.data] as unknown;
    return typeof v === 'number' && !isNaN(v) ? v : null;
  };
  // The CT/status field names are maintained on site and have already been
  // reworded several times ("Sump2 Pump1 Current" → "Sump2 Pump1" → "Sump2 P1"),
  // each rewording changing the mapFieldName() key. Resolve a channel by the
  // significant tokens of the meter's own field name instead of a fixed key, so
  // a rename no longer blanks the schematic. "Pump" collapses to "p" so that
  // Pump1/P1 normalise alike.
  // Split a field name into whole words, collapsing "Pump1"→"p1" so the Pump1/P1
  // spellings normalise alike. Matching compares whole words, never substrings:
  // "Sump2 P1" contains the letters "p2" (su-mp2-p1), so a substring test would
  // mis-resolve the P2 channel onto the P1 field.
  const words = (s: string) => s.toLowerCase().replace(/pump/g, 'p').replace(/[^a-z0-9]+/g, ' ').trim().split(' ');
  const findKey = (dev: Device | undefined, tokens: string[]): string | null => {
    const names = dev?.fieldNames;
    if (!names) return null;
    return Object.keys(names).find((k) => {
      const w = words(names[k]);
      return tokens.every((t) => w.includes(t));
    }) || null;
  };
  const findVal = (dev: Device | undefined, ...tokens: string[]): number | null => {
    const key = findKey(dev, tokens);
    return key ? val(dev, key) : null;
  };
  // The label a pump shows is the meter's own configured field name, so the
  // schematic always reads exactly like the device list.
  const findName = (dev: Device | undefined, ...tokens: string[]): string | null => {
    const key = findKey(dev, tokens);
    return key ? dev!.fieldNames![key] : null;
  };
  const baseSensor = byName('Mtech8_Sensor');
  const basePumpA = byName('MTech8Pump');
  const baseCurA = [byName('MTech8p_131_1'), byName('MTech8p_131_2'), byName('MTech8p_131_3')];
  const baseEnA = byName('MTech8p_2230');
  const basePumpB = byName('Mtech2Pump');
  const baseCurB = byName('MTech2p_131');
  const baseEnB = byName('Mtech2p_2230');

  // Poll each of the nine devices every 30s (shared cache/interval in the hook).
  // Disabled in demo mode so the showcase never hits the API. Nine fixed calls in
  // stable order — the Rules of Hooks are satisfied even before plants load
  // (undefined sn just yields null).
  const live = !demo;
  const latSensor = useDeviceLatest(baseSensor?.sn, live);
  const latPumpA = useDeviceLatest(basePumpA?.sn, live);
  const latCurA0 = useDeviceLatest(baseCurA[0]?.sn, live);
  const latCurA1 = useDeviceLatest(baseCurA[1]?.sn, live);
  const latCurA2 = useDeviceLatest(baseCurA[2]?.sn, live);
  const latEnA = useDeviceLatest(baseEnA?.sn, live);
  const latPumpB = useDeviceLatest(basePumpB?.sn, live);
  const latCurB = useDeviceLatest(baseCurB?.sn, live);
  const latEnB = useDeviceLatest(baseEnB?.sn, live);

  const sensor = mergeLive(baseSensor, latSensor);
  const pumpA = mergeLive(basePumpA, latPumpA);
  const curA = [mergeLive(baseCurA[0], latCurA0), mergeLive(baseCurA[1], latCurA1), mergeLive(baseCurA[2], latCurA2)];
  const enA = mergeLive(baseEnA, latEnA);
  const pumpB = mergeLive(basePumpB, latPumpB);
  const curB = mergeLive(baseCurB, latCurB);
  const enB = mergeLive(baseEnB, latEnB);

  const REFRESH_SEC = 30;
  // Countdown to the next poll. useDeviceLatest hands back a new response object
  // on every 30s fetch, so this effect fires once per refresh — we stamp when the
  // last batch landed and count down from there. A 1s ticker re-renders so the
  // number ticks; both run only while live.
  const refreshedAtRef = useRef<number | null>(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!live) return;
    refreshedAtRef.current = Date.now();
    setTick((t) => t + 1);
  }, [live, latSensor, latPumpA, latCurA0, latCurA1, latCurA2, latEnA, latPumpB, latCurB, latEnB]);
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [live]);
  const countdown = refreshedAtRef.current == null
    ? null
    : Math.max(0, REFRESH_SEC - Math.floor((Date.now() - refreshedAtRef.current) / 1000));

  // The eight Zone A loads in CT-channel order — 131_1 carries Sump2 P1/P2 + EQ1,
  // 131_2 EQ2 + Sludge + AE1, 131_3 AE2 + SED. The tokens match both the CT meter
  // (current) and MTech8Pump (on/off + overload).
  const LOAD_A: string[][] = [
    ['sump2', 'p1'], ['sump2', 'p2'], ['eq1'], ['eq2'], ['sludge'], ['ae1'], ['ae2'], ['sed'],
  ];
  const CT_A = [curA[0], curA[0], curA[0], curA[1], curA[1], curA[1], curA[2], curA[2]];
  const ampA = LOAD_A.map((t, i) => findVal(CT_A[i], ...t));
  // Labels come straight from the CT meter's field name — falling back to the
  // match tokens only if that channel is missing from the meter's config.
  const roleA = LOAD_A.map((t, i) => findName(CT_A[i], ...t) || t.join(' ').toUpperCase());
  const roleB = [
    findName(curB, 'sump1', 'p1') || 'Sump1 P1',
    findName(curB, 'sump1', 'p2') || 'Sump1 P2',
  ];
  const zoneA = Array.from({ length: 8 }, (_, i) => ({
    label: `OL${i + 1}`,
    role: roleA[i],
    on: demo ? DA[i].on : findVal(pumpA, ...LOAD_A[i], 'onoff'),
    ov: demo ? DA[i].ov : findVal(pumpA, ...LOAD_A[i], 'over'),
    amp: demo ? DA[i].a : ampA[i],
  }));
  const zoneB = [
    { label: 'OL1', role: roleB[0], on: demo ? DB2[0].on : findVal(pumpB, 'sump1', 'p1', 'onoff'), ov: demo ? DB2[0].ov : findVal(pumpB, 'sump1', 'p1', 'over'), amp: demo ? DB2[0].a : findVal(curB, 'sump1', 'p1') },
    { label: 'OL2', role: roleB[1], on: demo ? DB2[1].on : findVal(pumpB, 'sump1', 'p2', 'onoff'), ov: demo ? DB2[1].ov : findVal(pumpB, 'sump1', 'p2', 'over'), amp: demo ? DB2[1].a : findVal(curB, 'sump1', 'p2') },
  ];

  const kwA = demo ? 38.2 : val(enA, 'total_power');
  const kwhA = demo ? 742 : (val(enA, 'imp_energy') ?? val(enA, 'daily'));
  const kwB = demo ? 8.4 : val(enB, 'total_power');
  const kwhB = demo ? 168 : (val(enB, 'imp_energy') ?? val(enB, 'daily'));
  // Live power: a meter without a reading (offline) drops out; none → "—".
  const kwTotal = kwA == null && kwB == null ? null : (kwA ?? 0) + (kwB ?? 0);
  // Electricity energy + cost per zone. The cards run in process order — Zone A is
  // the Sump 1 panel (Mtech2p_2230) that feeds the plant, Zone B the eight-load
  // panel downstream (MTech8p_2230). Note this is the reverse of the internal
  // zoneA/zoneB naming above, which follows the pump panels: zoneA is the
  // eight-load panel. Keep the meter each card names in step with its figures.
  const ELEC_RATE = 4.2; // ฿/kWh — adjust per contract
  const zonesEnergy = [
    { name: th ? 'โซน A' : 'Zone A', meter: 'Mtech2p_2230', kw: kwB, today: demo ? 106 : (val(enB, 'daily') ?? 0), month: demo ? 2580 : (val(enB, 'monthly') ?? 0) },
    { name: th ? 'โซน B' : 'Zone B', meter: 'MTech8p_2230', kw: kwA, today: demo ? 512 : (val(enA, 'daily') ?? 0), month: demo ? 12240 : (val(enA, 'monthly') ?? 0) },
  ];
  const totToday = zonesEnergy.reduce((s, z) => s + z.today, 0);
  const totMonth = zonesEnergy.reduce((s, z) => s + z.month, 0);

  // Water quality + Thai industrial effluent standard compliance
  const wq = demo
    ? { do: 3.8, ph: 7.2, cod: 85, bod: 15, toc: 22, turb: 18 }
    : {
        do: findVal(sensor, 'do', 'concentration') ?? val(sensor, 'do'),
        ph: val(sensor, 'ph'),
        cod: val(sensor, 'cod'),
        bod: val(sensor, 'bod'),
        toc: val(sensor, 'toc'),
        turb: findVal(sensor, 'turbidity'),
      };
  const chk = {
    ph: wq.ph == null ? null : wq.ph >= 5.5 && wq.ph <= 9,
    cod: wq.cod == null ? null : wq.cod <= 120,
    bod: wq.bod == null ? null : wq.bod <= 20,
  };
  const checks = [chk.ph, chk.cod, chk.bod].filter((x) => x !== null) as boolean[];
  const compliant = checks.length > 0 && checks.every(Boolean);
  const hasWq = checks.length > 0;

  const allDevs = [sensor, pumpA, ...curA, enA, pumpB, curB, enB];
  const online = demo || allDevs.some((d) => d?.st === 'online');

  // `mut` is pushed toward the primary text colour so small schematic labels read
  // clearly in both themes (darker on light, lighter on dark). `BD` replaces the
  // near-invisible --border (8% alpha) for card/tank/pipe outlines.
  const C = { blue: 'var(--blue)', cyan: 'var(--cyan)', amber: 'var(--amber)', green: 'var(--green)', red: 'var(--red)', mut: 'color-mix(in srgb, var(--text-muted) 55%, var(--text-primary))' };
  const BD = 'color-mix(in srgb, var(--text-muted) 50%, transparent)';
  const fmt = (v: number | null, dp = 1) => (v == null ? NO_VALUE : v.toFixed(dp));

  // pump status: fault (overload) > running > stopped > unknown
  const pStat = (p: { on: number | null; ov: number | null }) => {
    if (p.ov != null && p.ov > 0) return { c: C.red, t: th ? 'โอเวอร์โหลด' : 'Overload', run: false, fault: true };
    if (p.on != null && p.on > 0) return { c: C.green, t: th ? 'ทำงาน' : 'Run', run: true, fault: false };
    if (p.on != null) return { c: C.mut, t: th ? 'หยุด' : 'Stop', run: false, fault: false };
    return { c: BD, t: NO_VALUE, run: false, fault: false };
  };
  const runCountA = zoneA.filter((p) => pStat(p).run).length;
  const faultA = zoneA.filter((p) => pStat(p).fault).length;
  const runCountB = zoneB.filter((p) => pStat(p).run).length;
  const faultB = zoneB.filter((p) => pStat(p).fault).length;

  // Pump groups along the treatment train (per the plant diagram)
  const gRun = (arr: typeof zoneA) => arr.filter((p) => pStat(p).run).length;
  const gFault = (arr: typeof zoneA) => arr.filter((p) => pStat(p).fault).length;
  const grpInlet = [zoneA[0], zoneA[1]];
  const grpSumTr = [zoneA[2]];
  const grpAer = [zoneA[3], zoneA[4]];
  const grpInter = [zoneA[5], zoneA[6]];
  const grpPostTr = [zoneA[7]];
  const grpOutlet = zoneB;
  // Prefer the "(DO) Concentration" channel (the actual dissolved-oxygen reading)
  // over the plain "DO" field; fall back to "DO" for sites without the concentration channel.
  const doVal = demo ? 3.8 : (findVal(sensor, 'do', 'concentration') ?? val(sensor, 'do'));

  const St = { fontFamily: 'system-ui, sans-serif' } as const;

  const PumpCard = ({ p }: { p: { label: string; role?: string; on: number | null; ov: number | null; amp: number | null } }) => {
    const overload = p.ov != null && p.ov > 0;
    const running = p.on != null && p.on > 0 && !overload;
    const known = p.on != null;
    const ovKnown = p.ov != null;
    const bodyC = overload ? C.red : running ? C.green : known ? C.mut : BD;
    const runTxt = overload ? (th ? 'โอเวอร์โหลด' : 'Overload') : running ? (th ? 'ทำงาน' : 'Run') : known ? (th ? 'หยุด' : 'Stop') : NO_VALUE;
    return (
      <div style={{ flex: '1 1 auto', minWidth: 106, border: `1px solid ${overload ? C.red : BD}`, borderRadius: 11, background: overload ? 'color-mix(in srgb, var(--red) 12%, var(--bg-elevated))' : 'var(--bg-elevated)', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
        {/* role + current */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 4 }}>
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.role ?? p.label}</span>
            <span style={{ fontSize: 8, color: C.mut }}>{p.label}</span>
          </span>
          <span style={{ fontSize: 13, fontWeight: 800, color: p.amp == null ? C.mut : 'var(--text-primary)', whiteSpace: 'nowrap' }}>{fmt(p.amp, 1)}<span style={{ fontSize: 8.5, color: C.mut, marginLeft: 2 }}>A</span></span>
        </div>
        {/* pump illustration (impeller spins while running) */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <svg viewBox="0 0 60 54" width={64} height={50}>
            <rect x={38} y={7} width={7} height={12} rx={1} fill="var(--bg-surface)" stroke={bodyC} strokeWidth={1.3} />
            <rect x={35} y={4} width={13} height={4} rx={1} fill={bodyC} />
            <rect x={4} y={44} width={50} height={6} rx={2} fill="var(--bg-surface)" stroke={BD} />
            <rect x={5} y={22} width={23} height={20} rx={3} fill="var(--bg-surface)" stroke={bodyC} strokeWidth={1.3} />
            <line x1={10} y1={24} x2={10} y2={40} stroke={bodyC} strokeWidth={0.8} opacity={0.5} />
            <line x1={14} y1={24} x2={14} y2={40} stroke={bodyC} strokeWidth={0.8} opacity={0.5} />
            <line x1={18} y1={24} x2={18} y2={40} stroke={bodyC} strokeWidth={0.8} opacity={0.5} />
            <line x1={28} y1={31} x2={33} y2={31} stroke={bodyC} strokeWidth={1.6} />
            <circle cx={42} cy={30} r={13} fill="var(--bg-surface)" stroke={bodyC} strokeWidth={2} />
            <rect x={53} y={26} width={6} height={8} rx={1} fill="var(--bg-surface)" stroke={bodyC} strokeWidth={1.1} />
            <g className={running ? 'wwtp-spin' : ''} style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
              {[0, 90, 180, 270].map((a) => (
                <path key={a} d="M42 30 q 5 -3.6 9.6 -2.4" fill="none" stroke={bodyC} strokeWidth={2} strokeLinecap="round" transform={`rotate(${a} 42 30)`} />
              ))}
              <circle cx={42} cy={30} r={3} fill={bodyC} />
            </g>
            {overload && <text x={42} y={49} fontSize={11} textAnchor="middle">⚠️</text>}
          </svg>
        </div>
        {/* run status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10 }}>
          <span className={running ? 'wwtp-pulse' : ''} style={{ width: 8, height: 8, borderRadius: 99, background: bodyC, boxShadow: running ? `0 0 6px ${bodyC}` : 'none' }} />
          <span style={{ color: C.mut }}>{th ? 'การทำงาน' : 'Status'}:</span>
          <span style={{ fontWeight: 700, color: bodyC }}>{runTxt}</span>
        </div>
        {/* overload status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10 }}>
          <span style={{ color: C.mut }}>OverLoad:</span>
          <span style={{ fontWeight: 700, color: overload ? C.red : ovKnown ? C.green : C.mut }}>{overload ? (th ? '⚠ ทริป' : '⚠ Trip') : ovKnown ? (th ? 'ปกติ' : 'OK') : NO_VALUE}</span>
        </div>
      </div>
    );
  };

  const Flow = ({ d, active, color }: { d: string; active: boolean; color: string }) => (
    <>
      <path d={d} fill="none" stroke={BD} strokeWidth={3} opacity={0.5} />
      {active && <path d={d} fill="none" stroke={color} strokeWidth={3} strokeDasharray="2 9" className="wwtp-flow" />}
    </>
  );

  const flowOn = online && (runCountA + runCountB > 0);

  // ── schematic building blocks ──
  // Animated water surface. A wave path repeats every 24px and the group scrolls
  // left by exactly one period (see the wwtpWave keyframe), so the motion loops
  // seamlessly. Two layers at different speeds give a little parallax. Must be
  // rendered inside the tank's clipPath so the horizontal overscan stays hidden.
  const WATER_FILL = 'color-mix(in srgb, var(--cyan) 22%, var(--bg-surface))';
  const wavePath = (x: number, w: number, wy: number, amp: number) => {
    const per = 24;
    const x0 = x - per;
    const span = w + per * 2;
    let d = `M ${x0} ${wy}`;
    for (let px = x0; px < x0 + span; px += per) d += ` q 6 -${amp} 12 0 t 12 0`;
    d += ` L ${x0 + span} ${wy + 400} L ${x0} ${wy + 400} Z`;
    return d;
  };
  const Water = ({ x, w, wy, bottom, amp = 2.4 }: { x: number; w: number; wy: number; bottom: number; amp?: number }) => (
    <>
      <rect x={x} y={wy} width={w} height={Math.max(0, bottom - wy)} fill={WATER_FILL} />
      <g className="wwtp-wave" style={{ animationDuration: '3.4s' }}>
        <path d={wavePath(x, w, wy + 1, amp * 0.8)} fill="color-mix(in srgb, var(--cyan) 14%, transparent)" />
      </g>
      <g className="wwtp-wave" style={{ animationDuration: '2.2s' }}>
        <path d={wavePath(x, w, wy, amp)} fill="none" stroke="color-mix(in srgb, var(--cyan) 60%, transparent)" strokeWidth={1.6} strokeLinecap="round" />
      </g>
    </>
  );

  const TankNode = ({ x, y, w, h, label, aer }: { x: number; y: number; w: number; h: number; label: string; aer?: boolean }) => {
    const wy = y + h * 0.42;
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} rx={4} fill="var(--bg-elevated)" stroke={BD} />
        <clipPath id={`tk${x}`}><rect x={x + 1} y={y + 1} width={w - 2} height={h - 2} rx={3} /></clipPath>
        <g clipPath={`url(#tk${x})`}>
          <Water x={x + 1} w={w - 2} wy={wy} bottom={y + h} />
          {aer && [0, 1, 2].map((i) => <circle key={i} className="wwtp-pulse" cx={x + w * 0.28 + i * (w * 0.22)} cy={y + h - 7} r={2.2} fill="color-mix(in srgb, var(--cyan) 75%, white)" />)}
        </g>
        <text x={x + w / 2} y={y + 14} fontSize={9} fontWeight={700} fill="var(--text-secondary)" textAnchor="middle">{label}</text>
      </g>
    );
  };
  const PumpNode = ({ x, y, w, h, label, group }: { x: number; y: number; w: number; h: number; label: string; group: typeof zoneA }) => {
    const run = gRun(group), fault = gFault(group), n = group.length;
    const known = group.some((p) => p.on != null);
    const col = fault ? C.red : run > 0 ? C.green : known ? C.mut : BD;
    const anyRun = run > 0 && !fault;
    const cx = x + 12, cy = y + h - 12;
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} rx={6} fill="var(--bg-elevated)" stroke={col} strokeWidth={1.2} />
        <circle cx={cx} cy={cy} r={7} fill="var(--bg-surface)" stroke={col} strokeWidth={1.3} />
        <g className={anyRun ? 'wwtp-spin' : ''} style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
          {[0, 120, 240].map((a) => <path key={a} d={`M${cx} ${cy} l 4.5 -2.6`} stroke={col} strokeWidth={1.5} strokeLinecap="round" transform={`rotate(${a} ${cx} ${cy})`} />)}
          <circle cx={cx} cy={cy} r={1.6} fill={col} />
        </g>
        <text x={x + w / 2 + 5} y={y + 13} fontSize={8} fontWeight={700} fill="var(--text-secondary)" textAnchor="middle">{label}</text>
        <text x={x + w - 6} y={y + h - 8} fontSize={11} fontWeight={800} fill={col} textAnchor="end">{run}/{n}</text>
      </g>
    );
  };
  // Full pump faceplate embedded in the schematic: illustration + run status +
  // overload status + current, drawn in SVG so every pump lives in the SCADA.
  const PumpFace = ({ x, y, p }: { x: number; y: number; p: (typeof zoneA)[number] }) => {
    const overload = p.ov != null && p.ov > 0;
    const running = p.on != null && p.on > 0 && !overload;
    const known = p.on != null;
    const col = overload ? C.red : running ? C.green : known ? C.mut : BD;
    const runTxt = overload ? (th ? 'โอเวอร์โหลด' : 'Overload') : running ? (th ? 'ทำงาน' : 'Run') : known ? (th ? 'หยุด' : 'Stop') : NO_VALUE;
    const w = 84, h = 68;
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} rx={6} fill="var(--bg-elevated)" stroke={overload ? C.red : BD} strokeWidth={overload ? 1.6 : 1} />
        {/* The role is the meter's own field name and can be longer than the
            faceplate — clip it so it can never run under the current reading.
            Sized to fit "Sump1 P1" (the longest real name) beside a 2-digit amp. */}
        <clipPath id={`pfr${x}-${y}`}><rect x={x + 4} y={y + 3} width={w - 38} height={12} /></clipPath>
        <text clipPath={`url(#pfr${x}-${y})`} x={x + 6} y={y + 12} fontSize={7.5} fontWeight={800} fill="var(--text-secondary)">{p.role}</text>
        <text x={x + 6} y={y + 21} fontSize={6.5} fill={C.mut}>{p.label}</text>
        <text x={x + w - 6} y={y + 13} fontSize={10.5} fontWeight={800} fill={p.amp == null ? C.mut : 'var(--text-primary)'} textAnchor="end">{fmt(p.amp, 1)}<tspan fontSize={7} fill={C.mut}> A</tspan></text>
        <svg x={x + 21} y={y + 18} width={42} height={34} viewBox="0 0 60 54">
          <rect x={38} y={7} width={7} height={12} rx={1} fill="var(--bg-surface)" stroke={col} strokeWidth={1.3} />
          <rect x={35} y={4} width={13} height={4} rx={1} fill={col} />
          <rect x={4} y={44} width={50} height={6} rx={2} fill="var(--bg-surface)" stroke={BD} />
          <rect x={5} y={22} width={23} height={20} rx={3} fill="var(--bg-surface)" stroke={col} strokeWidth={1.3} />
          <line x1={10} y1={24} x2={10} y2={40} stroke={col} strokeWidth={0.8} opacity={0.5} />
          <line x1={14} y1={24} x2={14} y2={40} stroke={col} strokeWidth={0.8} opacity={0.5} />
          <line x1={18} y1={24} x2={18} y2={40} stroke={col} strokeWidth={0.8} opacity={0.5} />
          <line x1={28} y1={31} x2={33} y2={31} stroke={col} strokeWidth={1.6} />
          <circle cx={42} cy={30} r={13} fill="var(--bg-surface)" stroke={col} strokeWidth={2} />
          <rect x={53} y={26} width={6} height={8} rx={1} fill="var(--bg-surface)" stroke={col} strokeWidth={1.1} />
          <g className={running ? 'wwtp-spin' : ''} style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
            {[0, 90, 180, 270].map((a) => <path key={a} d="M42 30 q 5 -3.6 9.6 -2.4" fill="none" stroke={col} strokeWidth={2} strokeLinecap="round" transform={`rotate(${a} 42 30)`} />)}
            <circle cx={42} cy={30} r={3} fill={col} />
          </g>
        </svg>
        <circle cx={x + 7} cy={y + h - 16} r={3} fill={col} className={running ? 'wwtp-pulse' : ''} />
        <text x={x + 13} y={y + h - 13} fontSize={8} fontWeight={700} fill={col}>{runTxt}</text>
        <text x={x + 6} y={y + h - 4} fontSize={7} fill={C.mut}>OL: <tspan fontWeight={700} fill={overload ? C.red : known ? C.green : C.mut}>{overload ? (th ? '⚠ ทริป' : '⚠ Trip') : known ? (th ? 'ปกติ' : 'OK') : NO_VALUE}</tspan></text>
      </g>
    );
  };

  return (
    <div style={{ ...St, position: 'relative', width: '100%', height: '100%', minHeight: 360, display: 'flex', flexDirection: 'column', gap: 8, containerType: 'inline-size' }}>
      <style>{`@keyframes wwtpMove { to { stroke-dashoffset: -22; } }
        .wwtp-flow { animation: wwtpMove .9s linear infinite; }
        @keyframes wwtpPulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        .wwtp-pulse { animation: wwtpPulse 1.3s ease-in-out infinite; }
        @keyframes wwtpWave { to { transform: translateX(-24px); } }
        .wwtp-wave { animation: wwtpWave 2.2s linear infinite; }
        @keyframes wwtpSpin { to { transform: rotate(360deg); } }
        .wwtp-spin { animation: wwtpSpin 1.1s linear infinite; transform-box: fill-box; transform-origin: center; }
        @media (prefers-reduced-motion: reduce){ .wwtp-flow,.wwtp-pulse,.wwtp-wave,.wwtp-spin{ animation:none; } }
        /* Plant photo — responsive to the WIDGET width (container query), not just
           the viewport, so it scales correctly at any dashboard column size. */
        .wwtp-photo {
          position: absolute; top: -118px; right: 30px; z-index: 2;
          width: clamp(180px, 28%, 360px); height: auto;
          object-fit: contain; pointer-events: none;
          filter: drop-shadow(0 6px 16px rgba(0,0,0,0.12));
        }
        @container (max-width: 1000px) {
          .wwtp-photo { width: clamp(190px, 34%, 380px); top: -24px; right: 22px; }
        }
        @container (max-width: 760px) {
          .wwtp-photo { width: clamp(150px, 30%, 260px); top: -6px; right: 10px;
            filter: drop-shadow(0 3px 8px rgba(0,0,0,0.12)); }
        }
        @container (max-width: 520px) {
          .wwtp-photo { display: none; }
        }`}</style>

      {/* Plant photo — decorative render pinned to the top-right whitespace,
          above the RAW/Filter/PURE tanks. pointer-events:none so it never
          blocks the SCADA interactions; drop the file at public/mtech-plant.png. */}
      <img
        className="wwtp-photo"
        src="/mtech-plant.png"
        alt="M-TECH wastewater treatment plant"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />

      {/* Project owner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
        <span style={{ fontWeight: 800, color: C.mut }}>🏛️ {th ? 'เจ้าของโครงการ' : 'Project owner'}:</span>
        <span style={{ fontWeight: 900, letterSpacing: 0.4, color: 'var(--text-primary)' }}>M-TECH WATER SOLUTION CO., LTD.</span>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700, color: online ? C.green : C.mut }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: online ? C.green : C.mut }} />
            {online ? (th ? 'ออนไลน์' : 'Online') : (th ? 'ออฟไลน์ (รอค่าจากอุปกรณ์)' : 'Offline (awaiting data)')}
          </span>
          <span style={{ color: C.mut }}>·</span>
          <span style={{ color: 'var(--text-secondary)' }}>{th ? 'ระบบบำบัดน้ำเสีย (S1/S2 → Treatment → RAW/PURE)' : 'Wastewater treatment train'}</span>
          {demo && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: '2px 7px', borderRadius: 99, color: C.amber, border: `1px solid ${C.amber}`, background: 'color-mix(in srgb, var(--amber) 14%, transparent)' }}>{th ? 'โหมดสาธิต' : 'DEMO'}</span>}
          {live && (() => {
            const refreshing = countdown === 0;
            const accent = countdown == null ? C.mut : refreshing ? C.amber : C.green;
            return (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 11px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                color: accent, border: `1.5px solid ${accent}`,
                background: `color-mix(in srgb, ${accent} 12%, transparent)`,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
                  className={refreshing ? 'wwtp-spin' : undefined}>
                  <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" />
                </svg>
                {countdown == null
                  ? (th ? 'กำลังเชื่อมต่อ…' : 'Connecting…')
                  : refreshing
                    ? (th ? 'กำลังรีเฟรช…' : 'Refreshing…')
                    : (<>{th ? 'รีเฟรชในอีก' : 'Refresh in'} <b style={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{countdown}</b> {th ? 'วิ' : 's'}</>)}
              </span>
            );
          })()}
        </div>
      </div>

      {/* Integrated SCADA — series topology: Sum1 → P1,P2 → Sum2 → P3,P4 → Treatment → filter/dose → PURE */}
      <svg viewBox="0 0 1120 330" width="100%" height="100%" style={{ display: 'block', flex: 1, minHeight: 0 }} preserveAspectRatio="xMidYMid meet">
        {/* flow connectors */}
        <Flow d="M54 84 L66 50" active={flowOn} color={C.cyan} />
        <Flow d="M54 120 L66 136" active={flowOn} color={C.cyan} />
        <Flow d="M150 50 L162 84" active={flowOn} color={C.cyan} />
        <Flow d="M150 136 L162 120" active={flowOn} color={C.cyan} />
        <Flow d="M210 84 L223 50" active={flowOn} color={C.cyan} />
        <Flow d="M210 120 L223 136" active={flowOn} color={C.cyan} />
        <Flow d="M307 50 L320 90" active={flowOn} color={C.cyan} />
        <Flow d="M307 136 L320 130" active={flowOn} color={C.cyan} />
        <Flow d="M620 110 L700 110" active={flowOn} color={C.blue} />
        <Flow d="M748 110 L828 110" active={flowOn} color={C.cyan} />
        <Flow d="M898 110 L980 110" active={flowOn} color={C.green} />
        <Flow d="M1028 110 L1112 110" active={flowOn} color={C.green} />
        {/* treatment tank → in-tank pump manifold (EQ1·EQ2·Sludge·AE1·AE2·SED) */}
        <line x1={470} y1={202} x2={470} y2={234} stroke={BD} strokeWidth={1.3} />
        <line x1={250} y1={234} x2={690} y2={234} stroke={BD} strokeWidth={1.3} />
        {[250, 338, 426, 514, 602, 690].map((mx, i) => <line key={i} x1={mx} y1={234} x2={mx} y2={240} stroke={BD} strokeWidth={1.3} />)}
        {/* PURE tank → effluent probe */}
        <line x1={1004} y1={194} x2={1004} y2={200} stroke={BD} strokeWidth={1.3} />

        {/* inlet sumps in series */}
        <TankNode x={6} y={44} w={48} h={150} label={th ? 'บ่อ Sum1' : 'Sump 1'} />
        <TankNode x={162} y={44} w={48} h={150} label={th ? 'บ่อ Sum2' : 'Sump 2'} />

        {/* Tank Treatment — multi-chamber vessel */}
        <rect x={320} y={36} width={300} height={166} rx={16} fill="var(--bg-elevated)" stroke={BD} />
        <clipPath id="trtClip"><rect x={322} y={107} width={296} height={93} rx={12} /></clipPath>
        <g clipPath="url(#trtClip)">
          <Water x={322} w={296} wy={112} bottom={202} amp={2.8} />
          {[522, 530, 538, 526, 534].map((bx, i) => <circle key={i} className="wwtp-pulse" cx={bx} cy={194 - i * 3} r={2} fill="color-mix(in srgb, var(--cyan) 75%, white)" />)}
        </g>
        {[380, 440, 500, 560].map((dx, i) => <line key={i} x1={dx} y1={42} x2={dx} y2={200} stroke={BD} strokeWidth={1} strokeDasharray="3 3" />)}
        <text x={470} y={31} fontSize={9} fontWeight={700} fill="var(--text-secondary)" textAnchor="middle">Tank Treatment</text>
        {([['Anaerobic', 350], ['Equal.', 410], ['Sludge', 470], [th ? 'เติมอากาศ' : 'Aeration', 530], [th ? 'ตกตะกอน' : 'Sed.', 590]] as [string, number][]).map(([lab, cx], i) => <text key={i} x={cx} y={52} fontSize={6.8} fill={C.mut} textAnchor="middle">{lab}</text>)}
        <rect x={500} y={150} width={60} height={16} rx={5} fill="var(--bg-elevated)" stroke={C.cyan} />
        <text x={530} y={162} fontSize={8.5} fontWeight={800} fill={C.cyan} textAnchor="middle">DO {fmt(doVal, 1)}<tspan fontSize={6.5} fontWeight={600} opacity={0.75}> mg/L</tspan></text>

        {/* water stage: Treatment →(P)→ RAW → Filter·Dosing →(P)→ PURE */}
        {([['RAW', 700], ['PURE', 980]] as [string, number][]).map(([lab, tx], i) => (
          <g key={i}>
            <rect x={tx} y={48} width={48} height={146} rx={6} fill="var(--bg-elevated)" stroke={i === 1 ? C.green : BD} />
            <ellipse cx={tx + 24} cy={48} rx={24} ry={4} fill="var(--bg-elevated)" stroke={BD} />
            <clipPath id={`vt${i}`}><rect x={tx + 1} y={99} width={46} height={95} /></clipPath>
            <g clipPath={`url(#vt${i})`}>
              <Water x={tx + 1} w={46} wy={104} bottom={194} amp={2.2} />
            </g>
            <text x={tx + 24} y={86} fontSize={9} fontWeight={700} fill="var(--text-secondary)" textAnchor="middle">{lab}</text>
          </g>
        ))}
        {/* Filter · Dosing module (single system) */}
        <rect x={828} y={48} width={70} height={146} rx={8} fill="var(--bg-elevated)" stroke={C.blue} />
        <text x={863} y={66} fontSize={8.5} fontWeight={700} fill="var(--text-secondary)" textAnchor="middle">{th ? 'ระบบกรอง' : 'Filter'}</text>
        <text x={863} y={78} fontSize={7.5} fill={C.mut} textAnchor="middle">{th ? 'เติมสาร' : 'Dosing'}</text>
        {[104, 120, 136, 152].map((fy, i) => <line key={i} x1={834} y1={fy} x2={892} y2={fy} stroke={C.blue} strokeWidth={1} opacity={0.5} strokeDasharray="2 3" />)}
        <circle cx={863} cy={176} r={3} fill={C.amber} className="wwtp-pulse" />

        <text x={6} y={38} fontSize={9} fill={C.mut}>{th ? 'น้ำเข้า' : 'In'}</text>
        <text x={1116} y={104} fontSize={8.5} fontWeight={700} fill={C.green} textAnchor="end">{th ? '→ น้ำใช้/ระบาย' : '→ Out'}</text>

        {/* PURE effluent — one row per parameter, each against its limit.
            BOD/COD are mg/L and pH is dimensionless; tbl_config_data carries no
            data_unit for this sensor, so the units are stated here. */}
        <rect x={914} y={200} width={180} height={74} rx={6} fill="var(--bg-elevated)" stroke={hasWq ? (compliant ? C.green : C.red) : BD} />
        <text x={1004} y={212} fontSize={7.5} fill="var(--text-secondary)" textAnchor="middle">{th ? 'น้ำ PURE (มาตรฐาน)' : 'PURE standard'}</text>
        {([
          ['BOD', wq.bod, 0, 'mg/L', chk.bod, '≤ 20', C.green],
          ['COD', wq.cod, 0, 'mg/L', chk.cod, '≤ 120', C.green],
          ['pH', wq.ph, 1, '', chk.ph, '5.5–9', C.green],
        ] as [string, number | null, number, string, boolean | null, string, string][]).map(([lab, v, dp, unit, ok, limit, okCol], i) => {
          const ry = 228 + i * 16;
          const col = ok === false ? C.red : ok === null ? C.mut : okCol;
          return (
            <g key={lab}>
              <text x={926} y={ry} fontSize={8.5} fontWeight={700} fill={col}>{lab}</text>
              <text x={958} y={ry} fontSize={7} fill={C.mut}>{limit}</text>
              <text x={1082} y={ry} fontSize={10.5} fontWeight={800} fill={col} textAnchor="end">{fmt(v, dp)}{unit && <tspan fontSize={7} fontWeight={600} opacity={0.75}> {unit}</tspan>}</text>
            </g>
          );
        })}

        {/* The 10 metered loads, each drawn where it physically sits: the two
            Sump 1 pumps lift to Sump 2, the two Sump 2 pumps feed the treatment
            tank, and the remaining six hang off the tank manifold under the
            chamber they serve. Treat→RAW and Filter→PURE have no CT, so no
            faceplate is drawn there. */}
        <PumpFace x={66} y={16} p={zoneB[0]} />
        <PumpFace x={66} y={102} p={zoneB[1]} />
        <PumpFace x={223} y={16} p={zoneA[0]} />
        <PumpFace x={223} y={102} p={zoneA[1]} />
        {/* manifold order follows the treatment train, not the OL numbering:
            EQ1·EQ2 → AE1·AE2 → Sludge → SED */}
        <PumpFace x={208} y={240} p={zoneA[2]} />
        <PumpFace x={296} y={240} p={zoneA[3]} />
        <PumpFace x={384} y={240} p={zoneA[5]} />
        <PumpFace x={472} y={240} p={zoneA[6]} />
        <PumpFace x={560} y={240} p={zoneA[4]} />
        <PumpFace x={648} y={240} p={zoneA[7]} />
      </svg>

      {/* Energy & electricity cost — cards per zone (2 meters) + total */}
      <div style={{ ...St, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {[
          { name: zonesEnergy[0].name, meter: zonesEnergy[0].meter, kw: zonesEnergy[0].kw, today: zonesEnergy[0].today, month: zonesEnergy[0].month, accent: C.cyan, icon: '🅰️', total: false },
          { name: zonesEnergy[1].name, meter: zonesEnergy[1].meter, kw: zonesEnergy[1].kw, today: zonesEnergy[1].today, month: zonesEnergy[1].month, accent: C.blue, icon: '🅱️', total: false },
          { name: th ? 'รวมทั้งระบบ' : 'Total', meter: th ? '2 มิเตอร์' : '2 meters', kw: kwTotal, today: totToday, month: totMonth, accent: C.green, icon: '⚡', total: true },
        ].map((z, i) => (
          <div key={i} style={{
            flex: '1 1 220px', minWidth: 208, borderRadius: 14,
            border: `1px solid ${z.total ? 'color-mix(in srgb, var(--green) 40%, var(--border))' : BD}`,
            background: z.total ? 'linear-gradient(150deg, color-mix(in srgb, var(--green) 12%, var(--bg-elevated)), var(--bg-elevated))' : 'var(--bg-elevated)',
            padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)' }}>{z.icon} {z.name}</span>
              <span style={{ fontSize: 8.5, color: C.mut }}>{z.meter}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <span style={{ fontSize: 25, fontWeight: 800, color: z.accent, lineHeight: 1 }}>{fmt(z.kw, 1)}</span>
              <span style={{ fontSize: 11, color: C.mut }}>kW</span>
              <span style={{ marginLeft: 'auto', fontSize: 9.5, color: C.mut }}>{th ? 'กำลังไฟ' : 'power'}</span>
            </div>
            <div style={{ display: 'flex', gap: 14, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
              <div><div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(z.today, 0)}<span style={{ fontSize: 9, color: C.mut, marginLeft: 2 }}>kWh</span></div><div style={{ fontSize: 9, color: C.mut }}>{th ? 'พลังงานวันนี้' : 'today'}</div></div>
              <div><div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(z.month, 0)}<span style={{ fontSize: 9, color: C.mut, marginLeft: 2 }}>kWh</span></div><div style={{ fontSize: 9, color: C.mut }}>{th ? 'เดือนนี้' : 'month'}</div></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
              <div><div style={{ fontSize: 9, color: C.mut }}>{th ? '💵 ค่าไฟเดือนนี้' : '💵 cost / month'}</div><div style={{ fontSize: 20, fontWeight: 800, color: C.green, lineHeight: 1.05 }}>฿{(z.month * ELEC_RATE).toLocaleString('th-TH', { maximumFractionDigits: 0 })}</div></div>
              <div style={{ textAlign: 'right' }}><div style={{ fontSize: 9, color: C.mut }}>{th ? 'วันนี้' : 'today'}</div><div style={{ fontSize: 12.5, fontWeight: 700, color: C.green }}>฿{(z.today * ELEC_RATE).toLocaleString('th-TH', { maximumFractionDigits: 0 })}</div></div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 9, color: C.mut, textAlign: 'right', marginTop: -4 }}>{th ? `* ค่าไฟคำนวณที่ ฿${ELEC_RATE}/หน่วย (kWh)` : `* @ ฿${ELEC_RATE}/kWh`}</div>

      {/* Power-by credit */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 10, paddingTop: 8, marginTop: 2, borderTop: '1px solid var(--border)' }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.mut }}>Power By</span>
        <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.7, color: 'var(--text-primary)' }}>MOMO&nbsp;INFINITECH&nbsp;CO.,&nbsp;LTD.</span>
      </div>
    </div>
  );
}
